/**
 * Страница профиля пользователя
 * apps/web/src/pages/ProfilePage.tsx
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile } from '../lib/user-api';
import { shareResult } from '../lib/share';
import { detectPlatform, isVK, isCoursesSite } from '../platform/detect';
import { useGameBalance } from '../hooks/useGameBalance';
import { usePurchaseStore } from '../stores/purchase';

// ==================== Типы ====================

interface Certificate {
  id: string;
  visibleId: string;
  collectionId: string;
  collectionName: string;
  collectionIcon: string;
  score: number;
  modulesCompleted: number;
  totalModules: number;
  completedAt: number;
}

interface Achievement {
  id: string;
  name: string;
  description?: string;
  emoji: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  xpReward: number;
  unlockedAt: number;
}

interface ProfileData {
  odizerId: string;
  displayName?: string;
  xp: number;
  level: number;
  xpForCurrentLevel: number;
  xpForNextLevel: number;
  xpProgress: number;
  rank: string;
  rankName: string;
  rankEmoji: string;
  totalCorrect: number;
  totalAnswered: number;
  accuracy: number;
  bestStreak: number;
  dailyStreak: number;
  dailyPlayedToday: boolean;
  globalPosition?: number;
  memberSince: string;
  certificates: Certificate[];
  certificatesCount: number;
  achievements: Achievement[];
  achievementsCount: number;
  totalAchievements: number;
  courseRank?: { name: string; emoji: string; sectionsDone: number; coursesDone: number };
}

// ==================== Компоненты ====================

function StatCard({ icon, value, label }: { icon: string; value: string | number; label: string }) {
  return (
    <div className="bg-white rounded-xl p-3 text-center shadow-sm">
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-xl font-bold text-gray-900">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function XPProgressBar({ xp, xpForCurrentLevel, xpForNextLevel, level }: {
  xp: number;
  xpForCurrentLevel: number;
  xpForNextLevel: number;
  level: number;
}) {
  const currentLevelXP = xp - xpForCurrentLevel;
  const levelRange = xpForNextLevel - xpForCurrentLevel;
  const progress = levelRange > 0 ? Math.min(100, (currentLevelXP / levelRange) * 100) : 100;

  return (
    <div className="w-full">
      <div className="flex justify-between text-sm text-gray-600 mb-1">
        <span>Уровень {level}</span>
        <span>{currentLevelXP} / {levelRange} XP</span>
      </div>
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function CertificateCard({ cert, onClick }: { cert: Certificate; onClick: () => void }) {
  const date = new Date(cert.completedAt).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const scoreColor = cert.score >= 90 ? 'text-green-600' : cert.score >= 70 ? 'text-blue-600' : 'text-gray-600';

  return (
    <button
      onClick={onClick}
      className="w-full bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 
                 border border-amber-200 hover:shadow-md transition-all text-left"
    >
      <div className="flex items-center gap-3">
        <div className="text-3xl">{cert.collectionIcon}</div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 truncate">{cert.collectionName}</h4>
          <p className="text-xs text-gray-500">{cert.visibleId}</p>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${scoreColor}`}>{cert.score}%</div>
          <div className="text-xs text-gray-500">{date}</div>
        </div>
      </div>
    </button>
  );
}

function AchievementBadge({ achievement, showDetails = false }: { 
  achievement: Achievement; 
  showDetails?: boolean;
}) {
  const rarityColors: Record<string, string> = {
    common: 'bg-gray-100 border-gray-300',
    rare: 'bg-blue-50 border-blue-300',
    epic: 'bg-purple-50 border-purple-300',
    legendary: 'bg-amber-50 border-amber-400',
  };

  const rarityGlow: Record<string, string> = {
    common: '',
    rare: 'shadow-blue-200',
    epic: 'shadow-purple-200',
    legendary: 'shadow-amber-200 shadow-lg',
  };

  return (
    <div 
      className={`rounded-xl p-3 border-2 ${rarityColors[achievement.rarity]} ${rarityGlow[achievement.rarity]} 
                  transition-transform hover:scale-105`}
      title={achievement.description}
    >
      <div className="text-center">
        <div className="text-2xl mb-1">{achievement.emoji}</div>
        {showDetails && (
          <>
            <div className="text-xs font-medium text-gray-900 truncate">{achievement.name}</div>
            <div className="text-xs text-gray-500">+{achievement.xpReward} XP</div>
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="text-center py-8">
      <div className="text-4xl mb-2">{icon}</div>
      <h3 className="font-medium text-gray-700">{title}</h3>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
  );
}

// ==================== Основной компонент ====================

export default function ProfilePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'stats' | 'certificates' | 'achievements'>('stats');
  const courses = isCoursesSite();
  const { balance, refetch: refetchBalance } = useGameBalance();
  const openPurchase = usePurchaseStore((s) => s.open);
  const setRefetchBalance = usePurchaseStore((s) => s.setRefetchBalance);

  useEffect(() => {
    setRefetchBalance(refetchBalance);
    return () => setRefetchBalance(null);
  }, [refetchBalance, setRefetchBalance]);

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    try {
      setLoading(true);
      const data = await getProfile();
      setProfile(data);
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Не удалось загрузить профиль');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4 animate-bounce">👤</div>
          <p className="text-gray-600">Загрузка профиля...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">😕</div>
          <p className="text-gray-600 mb-4">{error || 'Профиль не найден'}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg"
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    const platform = detectPlatform();
    const text = `🏆 Мой профиль в BESHEK!\n\n${profile.rankEmoji} ${profile.rankName} • Уровень ${profile.level}\n📊 ${profile.xp} XP\n✅ Точность: ${profile.accuracy}%\n📜 Дипломов: ${profile.certificatesCount}\n🏅 Достижений: ${profile.achievementsCount}`;
    
    let url: string;
    if (platform === 'vk') {
      url = 'https://vk.com/app52236695#profile';
    } else if (platform === 'telegram') {
      url = 'https://t.me/qwizmug_bot?start=profile';
    } else {
      url = 'https://beshek.mugstel.ru/profile';
    }
    
    shareResult({ text, url });








  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className={`${courses ? 'bg-[#0F766E]' : 'bg-gradient-to-br from-blue-500 to-purple-600'} text-white p-6 pb-16`}>
        <button
          onClick={() => window.location.href = '/'}
          className="mb-4 flex items-center gap-2 text-white/80 hover:text-white"
        >
          ← Назад
        </button>

        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-3xl">
            {courses && profile.courseRank ? profile.courseRank.emoji : profile.rankEmoji}
          </div>
          <div>
            <h1 className="text-xl font-bold">{profile.displayName || (courses ? 'Ученик' : 'Игрок')}</h1>
            <p className="text-white/80">{courses && profile.courseRank ? profile.courseRank.name : `${profile.rankName} • Уровень ${profile.level}`}</p>
            {!courses && profile.globalPosition && (
              <p className="text-white/70 text-sm">#{profile.globalPosition} в рейтинге</p>
            )}
          </div>
        </div>
      </div>

      {courses && profile.courseRank ? (
        <div className="mx-4 -mt-10 bg-white rounded-2xl p-4 shadow-lg">
          <div className="grid grid-cols-2 gap-3 text-center">
            <div><div className="text-2xl font-bold text-gray-900">{profile.courseRank.sectionsDone}</div><div className="text-xs text-gray-500">разделов сдано</div></div>
            <div><div className="text-2xl font-bold text-gray-900">{profile.courseRank.coursesDone}</div><div className="text-xs text-gray-500">курсов пройдено</div></div>
          </div>
          <p className="mt-3 text-center text-xs text-gray-500">
            {profile.courseRank.coursesDone >= 1 ? 'Практик: пройден целый курс' : profile.courseRank.sectionsDone >= 3 ? 'Уверенный: до «практика» — пройти курс целиком' : `Новичок: до «уверенного» — ${Math.max(0, 3 - profile.courseRank.sectionsDone)} раздел(а)`}
          </p>
        </div>
      ) : (
      <div className="mx-4 -mt-10 bg-white rounded-2xl p-4 shadow-lg">
        <XPProgressBar
          xp={profile.xp}
          xpForCurrentLevel={profile.xpForCurrentLevel}
          xpForNextLevel={profile.xpForNextLevel}
          level={profile.level}
        />
        <div className="mt-3 text-center">
          <span className="text-2xl font-bold text-gray-900">{profile.xp.toLocaleString()}</span>
          <span className="text-gray-500 ml-1">XP</span>
        </div>
      </div>
      )}

      {/* Баланс игр */}
      {!courses && balance && (
        <div className="mx-4 mt-4 bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <div className="text-gray-900 font-bold">
              🎮 Игр осталось: {balance.paidGames + balance.freeGamesLeft}
            </div>
            <div className="text-gray-500 text-xs mt-1">
              Бесплатных: {balance.freeGamesLeft} • Платных: {balance.paidGames}
            </div>
          </div>
          {isVK() && (
            <button
              onClick={openPurchase}
              className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold rounded-xl shadow hover:scale-[1.03] transition-all active:scale-95"
            >
              Купить
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 px-4 mt-6 mb-4">
        {[
          { id: 'stats', label: '📊 Статистика' },
          ...(detectPlatform() !== 'vk' ? [{ id: 'certificates', label: `📜 Дипломы (${profile.certificatesCount})` }] : []),
          { id: 'achievements', label: `🏆 Бейджи (${profile.achievementsCount})` },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as 'stats' | 'certificates' | 'achievements')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-4">
        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <StatCard icon="✅" value={profile.totalCorrect} label="Правильных" />
              <StatCard icon="🎯" value={`${profile.accuracy}%`} label="Точность" />
              <StatCard icon="🔥" value={profile.bestStreak} label="Лучший страйк" />
              {courses && profile.courseRank
                ? <StatCard icon="📘" value={profile.courseRank.sectionsDone} label="Разделов сдано" />
                : <StatCard icon="📅" value={profile.dailyStreak} label="Дней подряд" />}
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">📈 Общая статистика</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Всего ответов</span>
                  <span className="font-medium">{profile.totalAnswered}</span>
                </div>
                {detectPlatform() !== 'vk' && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Сертификатов</span>
                  <span className="font-medium">{profile.certificatesCount}</span>
                </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Достижений</span>
                  <span className="font-medium">{profile.achievementsCount} / {profile.totalAchievements}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">С нами с</span>
                  <span className="font-medium">
                    {new Date(profile.memberSince).toLocaleDateString('ru-RU')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Certificates Tab */}
        {activeTab === 'certificates' && (
          <div className="space-y-3">
            {profile.certificates.length > 0 ? (
              profile.certificates.map((cert) => (
                <CertificateCard
                  key={cert.id}
                  cert={cert}
                  onClick={() => console.log('Certificate:', cert)}
                />
              ))
            ) : (
              <EmptyState
                icon="📜"
                title="Пока нет сертификатов"
                subtitle="Пройди курс чтобы получить свой первый диплом!"
              />
            )}

            {profile.certificates.length === 0 && (
              <button
                onClick={() => navigate('/collections')}
                className="w-full py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
              >
                Перейти к курсам
              </button>
            )}
          </div>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <div className="space-y-4">
            {profile.achievements.length > 0 ? (
              <div className="grid grid-cols-3 gap-3">
                {profile.achievements.map((ach) => (
                  <AchievementBadge 
                    key={ach.id} 
                    achievement={ach}
                    showDetails
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="🏆"
                title="Пока нет достижений"
                subtitle="Играй в викторины чтобы заработать бейджи!"
              />
            )}

            <button
              onClick={() => console.log('Show all achievements')}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Все достижения ({profile.achievementsCount}/{profile.totalAchievements})
            </button>
          </div>
        )}
      </div>

      {/* Share Button */}
      <div className="fixed bottom-4 left-4 right-4">
        <button
          onClick={handleShare}
          className={`w-full py-3 ${courses ? 'bg-[#0F766E]' : 'bg-gradient-to-r from-blue-500 to-purple-500'} text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all`}
        >
          📤 Поделиться профилем
        </button>
      </div>
    </div>
  );
}